const statusLabels = {
    transactional: "ترسل عند التسجيل",
    waiting_for_recipients: "بانتظار أول تسجيل",
    scheduled: "مجدولة",
    sent: "تم الإرسال",
    suspended: "متوقفة",
    skipped: "تم تخطيها",
    cancelled: "ملغاة",
    archived: "مؤرشفة",
};

function formatDateTime(value) {
    if (!value) return "غير محدد";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "غير محدد";

    return new Intl.DateTimeFormat("ar", {
        dateStyle: "medium",
        timeStyle: "short",
        numberingSystem: "latn",
    }).format(new Date(value));
}

function campaignScheduleLabel(campaign) {
    if (campaign.scheduledAt) {
        return formatDateTime(campaign.scheduledAt);
    }

    if (campaign.trigger === "on_registration" || campaign.status === "transactional") {
        return "عند تسجيل العميل";
    }

    return "غير محدد";
}

function formatMetric(value) {
    if (value === null || value === undefined || value === "") return "—";

    const numericValue = Number(value);

    return new Intl.NumberFormat("en-US").format(Number.isFinite(numericValue) ? numericValue : 0);
}

function formatPercent(value) {
    if (value === null || value === undefined || value === "") return "—";

    const numericValue = Number(value);

    return `${new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 2,
    }).format(Number.isFinite(numericValue) ? numericValue : 0)}%`;
}

function analyticsSourceLabel(source) {
    return source === "transactional"
        ? "رسائل المعاملات"
        : "حملة Brevo";
}

function CampaignAnalytics({analytics}) {
    if (!analytics) {
        return (
            <p className="webinar-campaign-analytics-message">
                لم يتم تحميل إحصاءات هذه الحملة بعد.
            </p>
        );
    }

    if (analytics.state === "not_applicable") {
        return (
            <p className="webinar-campaign-analytics-message">
                {analytics.message || "لا يتوفر تقرير Brevo لهذه الرسالة."}
            </p>
        );
    }

    if (analytics.state !== "available") {
        return (
            <p className="webinar-campaign-analytics-message webinar-campaign-analytics-message--error">
                {analytics.message || "تعذر تحميل إحصاءات هذه الحملة من Brevo الآن."}
            </p>
        );
    }

    const statistics = analytics.statistics || {};
    const campaign = analytics.campaign || null;
    const metricItems = [
        ["الرسائل المرسلة", formatMetric(statistics.sent)],
        ["تم التسليم", formatMetric(statistics.delivered)],
        ["الرسائل المفتوحة", formatMetric(statistics.uniqueOpens)],
        ["نسبة الفتح", formatPercent(statistics.openRate)],
        ["إلغاء الاشتراك", formatMetric(statistics.unsubscriptions)],
    ];

    return (
        <div className="webinar-campaign-analytics">
            <div className="webinar-campaign-analytics-heading">
                <p>إحصاءات الأداء</p>
                <span>{analyticsSourceLabel(analytics.source)}</span>
            </div>

            <dl className="webinar-campaign-metrics">
                {metricItems.map(([label, value]) => (
                    <div key={label}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                    </div>
                ))}
            </dl>

            {analytics.range ? (
                <p className="webinar-campaign-analytics-range">الفترة: {analytics.range}</p>
            ) : null}

            {campaign ? (
                <details className="webinar-campaign-details">
                    <summary>كل تفاصيل الحملة</summary>
                    <dl>
                        {[
                            ["الموضوع", campaign.subject],
                            ["حالة Brevo", campaign.status],
                            ["النوع", campaign.type],
                            ["تاريخ الإنشاء", formatDateTime(campaign.createdAt)],
                            ["آخر تعديل", formatDateTime(campaign.modifiedAt)],
                            ["موعد الجدولة", formatDateTime(campaign.scheduledAt)],
                            ["تاريخ الإرسال", formatDateTime(campaign.sentAt)],
                            ["المرسل", campaign.sender?.email || campaign.sender?.name],
                            ["الرد إلى", campaign.replyTo],
                            ["القوائم المستهدفة", campaign.recipients?.lists?.join(", ")],
                            ["قوائم الاستثناء", campaign.recipients?.exclusionLists?.join(", ")],
                            ["الوسوم", campaign.tags?.join(", ") || campaign.tag],
                        ].map(([label, value]) => value ? (
                            <div key={label}>
                                <dt>{label}</dt>
                                <dd>{value}</dd>
                            </div>
                        ) : null)}
                        {campaign.shareLink ? (
                            <div>
                                <dt>رابط المشاركة</dt>
                                <dd>
                                    <a href={campaign.shareLink} target="_blank" rel="noreferrer">
                                        فتح رابط المشاركة
                                    </a>
                                </dd>
                            </div>
                        ) : null}
                    </dl>
                </details>
            ) : null}
        </div>
    );
}

export default function WebinarCampaignList({campaigns = [], isRefreshing = false, onRefresh}) {
    return (
        <section className="webinar-campaign-list-section" aria-labelledby="webinar-campaign-list-title">
            <header className="webinar-campaign-list-heading">
                <div>
                    <p>حملات البريد</p>
                    <h2 id="webinar-campaign-list-title">قائمة حملات الويبينار</h2>
                </div>
                <span className="webinar-campaign-list-count">
                    {formatMetric(campaigns.length)} {campaigns.length === 1 ? "حملة" : "حملات"}
                </span>
                {onRefresh ? (
                    <button
                        className="webinar-campaign-refresh"
                        type="button"
                        onClick={onRefresh}
                        disabled={isRefreshing}
                    >
                        {isRefreshing ? "جاري التحديث..." : "تحديث بيانات Brevo"}
                    </button>
                ) : null}
            </header>

            {campaigns.length === 0 ? (
                <p className="webinar-campaign-list-empty">
                    لا توجد حملات بريد محفوظة لهذا الويبينار حتى الآن.
                </p>
            ) : (
                <ul className="webinar-campaign-list">
                    {campaigns.map((campaign, index) => {
                        const title = campaign.label || campaign.name || "حملة بريد";
                        const itemKey = campaign.campaignId || campaign.key || index;

                        return (
                            <li className="webinar-campaign-list-item" key={itemKey}>
                                <div className="webinar-campaign-list-item-heading">
                                    <h3>{title}</h3>
                                    <span className={`webinar-campaign-status webinar-campaign-status--${campaign.status || "unknown"}`}>
                                        {statusLabels[campaign.status] || campaign.status || "غير محددة"}
                                    </span>
                                </div>

                                {campaign.name && campaign.name !== title ? (
                                    <p className="webinar-campaign-list-name">{campaign.name}</p>
                                ) : null}

                                <dl className="webinar-campaign-list-details">
                                    <div>
                                        <dt>موعد الإرسال</dt>
                                        <dd>{campaignScheduleLabel(campaign)}</dd>
                                    </div>
                                    {campaign.campaignId ? (
                                        <div>
                                            <dt>معرّف Brevo</dt>
                                            <dd dir="ltr">{campaign.campaignId}</dd>
                                        </div>
                                    ) : null}
                                </dl>

                                <CampaignAnalytics analytics={campaign.analytics}/>

                                {campaign.reason ? (
                                    <p className="webinar-campaign-list-message">{campaign.reason}</p>
                                ) : null}
                                {campaign.lastError ? (
                                    <p className="webinar-campaign-list-message webinar-campaign-list-message--error">
                                        {campaign.lastError}
                                    </p>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
